'use client';

import { AdBreak, Profile } from '@vimond/services/player-profile/types';
import { createContext, ReactNode, useContext } from 'react';
import { usePlayerProfiles } from '../hooks/usePlayerProfiles';

type PlayerProfilesContextType = {
  getAdBreaksForProfile: (adProfileId?: string) => AdBreak[] | undefined;
  isLoading: boolean;
  playerProfiles: Profile[] | undefined;
};

const PlayerProfilesContext = createContext<
  PlayerProfilesContextType | undefined
>(undefined);

type Props = {
  children: ReactNode;
};

/**
 * Provider component that fetches and caches player profiles at the app level.
 * This ensures player profiles are fetched once and shared across all video components.
 */
export const PlayerProfilesProvider = ({ children }: Props) => {
  const playerProfilesData = usePlayerProfiles();

  return (
    <PlayerProfilesContext.Provider value={playerProfilesData}>
      {children}
    </PlayerProfilesContext.Provider>
  );
};

/**
 * Hook to access player profiles context.
 * Must be used within a PlayerProfilesProvider.
 */
export const usePlayerProfilesContext = () => {
  const context = useContext(PlayerProfilesContext);
  if (context === undefined) {
    throw new Error(
      'usePlayerProfilesContext must be used within a PlayerProfilesProvider',
    );
  }
  return context;
};
